/**
 * This file is the entry point for the React app, it sets up the root
 * element and renders the App component to the DOM.
 *
 * It is included in `src/index.html`.
 */

import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { RouterProvider, createRouter } from "@tanstack/react-router";

import { routeTree } from "@/routeTree.gen";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";

import "./styles/index.css";

import { ThemeProvider } from "./providers/ThemeProvider";
import { AuthProvider, useAuthenticate } from "./providers/AuthProvider";
import { WebSocketProvider } from "./websocket/WebsocketProvider";

const router = createRouter({
  routeTree,
  defaultPreload: "intent",
  scrollRestoration: true,
  context: { auth: undefined! },
});

// Register the router instance for type safety
declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

const rootElement = document.getElementById("root")!;

const queryClient = new QueryClient();

export const InnerApp = () => {
  const auth = useAuthenticate();
  return <RouterProvider router={router} context={{ auth }} />;
};

export const App = () => {
  return (
    <WebSocketProvider>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <ThemeProvider defaultTheme="dark" storageKey="ui-theme">
            <InnerApp />
          </ThemeProvider>
        </AuthProvider>
      </QueryClientProvider>
    </WebSocketProvider>
  );
};

const app = (
  <StrictMode>
    <App />
  </StrictMode>
);

if (import.meta.hot) {
  // With hot module reloading, `import.meta.hot.data` is persisted.
  const root = (import.meta.hot.data.root ??= createRoot(rootElement));
  root.render(app);
} else {
  // The hot module reloading API is not available in production.
  createRoot(rootElement).render(app);
}
